import { useState } from 'react';
import { ButtonSpinner, InputGroup, InputLeftElement } from '@chakra-ui/react';
import { FixedSizeList } from 'react-window';
import { useManagedTeams } from 'hooks/useManagedTeams';
import { Team } from './types';
import { TeamItem } from './TeamItem';
import { CbInput } from '../Inputs/CbInput';
import { CbButton } from '../Buttons/CbButton';
import styles from './CbTeamPicker.module.css';
import { ICON_BY_NAME } from '../Icons/Icon';
export interface CbTeamPickerProps {
  onChange: (players: Team[]) => void;
  value: Team[];
  disabled?: boolean;
  endpoint?: string;
  TeamListComponent?: any;
  notificationsForUserID?: number;
  displayLocationWarning?: boolean;
  disconnectedTeamIds?: number[];
}
const VirtualizedTeamList = ({
  items,
  height = 400,
  itemSize = 48,
  TeamListComponent,
  isConnectedToIntegration,
  displayLocationWarning,
  ...itemProps
}) => {
  if (!items?.length) {
    return <div className={styles.empty}>No teams available.</div>;
  }
  return <FixedSizeList height={height} width="100%" itemCount={items.length} itemSize={itemSize} data-sentry-element="FixedSizeList" data-sentry-component="VirtualizedTeamList" data-sentry-source-file="CbTeamPicker.tsx">
      {({
      index,
      style
    }) => <div style={{
      ...style,
      backgroundColor: index % 2 === 1 ? 'transparent' : 'var(--chakra-colors-cloud-100)'
    }}>
          <TeamListComponent key={items[index].id} team={items[index]} isConnectedToIntegration={isConnectedToIntegration(items[index])} displayLocationWarning={displayLocationWarning} {...itemProps} />
        </div>}
    </FixedSizeList>;
};
export const CbTeamPicker = ({
  onChange,
  value,
  disabled = false,
  endpoint,
  TeamListComponent = TeamItem,
  notificationsForUserID,
  displayLocationWarning = true,
  disconnectedTeamIds = []
}: CbTeamPickerProps) => {
  const isConnectedToIntegration = (team: Team) => {
    return !disconnectedTeamIds.includes(team.id);
  };

  // Searching:
  const [search, setSearch] = useState<string>();

  //
  // Fetching teams
  const {
    teams: fullTeams,
    loading: loading
  } = useManagedTeams(endpoint);
  const teams = fullTeams as Team[];
  // valueIds is useful for testing team membership, given that team
  // objects are not immutable and so might not be equal even if they are
  // the same team
  const valueIds = value.map(p => p.id);
  // Can't select teams you've already selected, or that don't match the search
  const selectableTeams = teams?.filter(p => !valueIds.includes(p.id) && p.name.toLocaleLowerCase().includes(search?.toLocaleLowerCase() || ''));

  //
  // Actions
  const addTeam = (team: Team) => {
    if (valueIds.includes(team.id)) return;
    onChange([team, ...value]);
  };
  const removeTeam = (team: Team) => {
    onChange(value.filter(p => p.id !== team.id));
  };
  const clear = () => {
    onChange([]);
  };
  const addAll = () => {
    onChange([...value, ...selectableTeams]);
  };
  if (disabled) {
    return <div className={styles.column}>
        <VirtualizedTeamList items={value} TeamListComponent={TeamListComponent} disabled={disabled} isConnectedToIntegration={isConnectedToIntegration} displayLocationWarning={displayLocationWarning} />
      </div>;
  }

  /*
    If the viewing user is a team manager they may not have access to all of the teams that the current user belongs to.
    In this case we want to prevent them from removing the user from all teams and thus disable the clear button
  */

  const canRemoveAllTeams = value.filter(currentTeam => !teams?.find(managedTeam => managedTeam.id == currentTeam.id)).length == 0;
  return <div data-sentry-component="CbTeamPicker" data-sentry-source-file="CbTeamPicker.tsx">
      <div className={styles.searchBar}>
        <InputGroup data-sentry-element="InputGroup" data-sentry-source-file="CbTeamPicker.tsx">
          <InputLeftElement pointerEvents="none" children={<ICON_BY_NAME.search />} sx={{
          h: 'var(--chakra-sizes-10)',
          w: 'var(--chakra-sizes-10)'
        }} data-sentry-element="InputLeftElement" data-sentry-source-file="CbTeamPicker.tsx" />
          <CbInput aria-label="Learner name" placeholder="Search" paddingLeft="2.5rem" onChange={e => setSearch(e.target.value)} value={search} data-sentry-element="CbInput" data-sentry-source-file="CbTeamPicker.tsx" />
        </InputGroup>
      </div>

      <div className={styles.columns}>
        <div className={styles.columnTop}>
          <span className={styles.columnTitle}>Add teams</span>
          {loading && <ButtonSpinner position="relative" />}
          <CbButton size="sm" onClick={addAll} data-sentry-element="CbButton" data-sentry-source-file="CbTeamPicker.tsx">
            Add All
          </CbButton>
        </div>

        <div className={styles.columnTop}>
          <span className={styles.columnTitle}>Selected teams</span>
          {canRemoveAllTeams && <CbButton size="sm" variant="outline" onClick={clear}>
              Clear
            </CbButton>}
        </div>

        <div className={styles.column}>
          <VirtualizedTeamList items={selectableTeams} TeamListComponent={TeamListComponent} onAdd={addTeam} isConnectedToIntegration={isConnectedToIntegration} displayLocationWarning={displayLocationWarning} data-sentry-element="VirtualizedTeamList" data-sentry-source-file="CbTeamPicker.tsx" />
        </div>

        <div className={styles.column}>
          <VirtualizedTeamList items={value} TeamListComponent={TeamListComponent} onRemove={removeTeam} isConnectedToIntegration={isConnectedToIntegration} displayLocationWarning={displayLocationWarning} renderItem={team => {
          const canEdit = !!teams?.find(managedTeam => managedTeam.id == team.id);
          return {
            disabled: disabled || !canEdit,
            notificationsForUserID: canEdit ? notificationsForUserID : undefined
          };
        }} data-sentry-element="VirtualizedTeamList" data-sentry-source-file="CbTeamPicker.tsx" />
        </div>
      </div>
    </div>;
};